import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router';

import { ROUTES } from '@/app/router/routes';
import { AlertCircleIcon, MailIcon } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { TextField } from '@/components/ui/TextField';
import { ApiErrorCode } from '@/lib/api';

import { useAuth } from '../context/auth-context';
import { useLogin } from '../hooks/useLogin';
import { loginSchema, type LoginFormValues } from '../schemas/login.schema';
import { PasswordField } from './PasswordField';

/** Traduit le code d'erreur serveur en message affichable. */
const errorMessage = (code: string): string => {
  switch (code) {
    case ApiErrorCode.INVALID_CREDENTIALS:
      // Message volontairement indifférencié : préciser « cet e-mail n'existe
      // pas » permettrait d'énumérer les comptes de la plateforme.
      return 'Adresse e-mail ou mot de passe incorrect.';
    case ApiErrorCode.ACCOUNT_DISABLED:
      return 'Ce compte a été désactivé. Contactez votre administrateur.';
    case ApiErrorCode.EMAIL_NOT_VERIFIED:
      return 'Vérifiez votre adresse e-mail avant de vous connecter.';
    case ApiErrorCode.RATE_LIMITED:
      return 'Trop de tentatives. Patientez quelques minutes avant de réessayer.';
    case ApiErrorCode.NETWORK_ERROR:
      return 'Impossible de joindre le serveur. Vérifiez votre connexion.';
    default:
      return 'La connexion a échoué. Réessayez dans quelques instants.';
  }
};

export const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useAuth();

  /**
   * Destination après connexion.
   *
   * Si l'utilisateur a été renvoyé ici par `RequireAuth` en tentant d'ouvrir
   * une page protégée, on l'y ramène ; sinon, tableau de bord. Le `typeof` est
   * une garde réelle : `location.state` provient de l'historique du navigateur,
   * qui est manipulable, il ne peut pas être tenu pour typé.
   */
  const state = location.state as { from?: unknown; passwordChanged?: boolean } | null;
  const redirectTo = typeof state?.from === 'string' ? state.from : ROUTES.dashboard;

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', rememberMe: true },
    // Valide au premier envoi, puis à chaque frappe : pas de message d'erreur
    // affiché avant que l'utilisateur ait eu l'occasion de finir de saisir.
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const login = useLogin({
    onSuccess: (session) => {
      // Renseigner le contexte avant de naviguer : sans cela, `RequireAuth`
      // évaluerait la route d'arrivée alors que le statut est encore
      // `anonymous`, et renverrait aussitôt vers la connexion.
      setSession(session.accessToken, session.user);
      // `replace` : la page de connexion sort de l'historique, le bouton
      // « retour » ne doit pas y ramener un utilisateur déjà connecté.
      void navigate(redirectTo, { replace: true });
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await login.mutateAsync(values);
    } catch (error) {
      // Les erreurs par champ renvoyées par le serveur sont repositionnées
      // sous le champ concerné ; le reste devient un message global.
      const fieldErrors = login.error?.fieldErrors ?? {};
      for (const [field, message] of Object.entries(fieldErrors)) {
        if (field === 'email' || field === 'password') setError(field, { message });
      }
      void error;
    }
  });

  const globalError =
    login.error && Object.keys(login.error.fieldErrors).length === 0
      ? errorMessage(login.error.code)
      : null;

  return (
    <form
      onSubmit={(event) => void onSubmit(event)}
      noValidate
      // Espacement fluide : se resserre sur un écran court pour que le
      // formulaire tienne dans la fenêtre sans défilement.
      className="space-y-[clamp(0.625rem,2vh,1.25rem)]"
    >
      {(location.state as { passwordChanged?: boolean } | null)?.passwordChanged === true && (
        <p
          role="status"
          className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800"
        >
          Votre mot de passe a été modifié. Reconnectez-vous avec votre nouveau mot de passe.
        </p>
      )}
      {globalError ? (
        <div
          role="alert"
          aria-atomic="true"
          className="flex items-start gap-3 rounded-lg border border-red-200/80 bg-red-50/60 bg-linear-to-b from-white/80 to-transparent p-2 pr-4 shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95),inset_0_-1px_0_0_rgb(127_29_29/0.03),0_2px_4px_-1px_rgb(127_29_29/0.08)]"
        >
          <span
            aria-hidden
            className="grid size-8 shrink-0 place-items-center rounded-md bg-red-100/60 text-[17px] text-red-600"
          >
            <AlertCircleIcon />
          </span>
          <p className="min-w-0 self-center py-1 text-sm leading-5 font-medium text-red-800">
            {globalError}
          </p>
        </div>
      ) : null}

      <TextField
        label="Adresse e-mail"
        type="email"
        placeholder="votre@email.com"
        icon={<MailIcon />}
        autoComplete="email"
        // Fait apparaître le clavier e-mail sur mobile et évite la majuscule
        // automatique, qui produit des saisies invalides sur iOS.
        autoCapitalize="none"
        spellCheck={false}
        error={errors.email?.message}
        {...register('email')}
      />

      <PasswordField
        label="Mot de passe"
        placeholder="Votre mot de passe"
        autoComplete="current-password"
        error={errors.password?.message}
        {...register('password')}
      />

      <div className="flex items-center justify-between gap-4">
        <Checkbox label="Se souvenir de moi" {...register('rememberMe')} />
        <Link
          to={ROUTES.forgotPassword}
          className="text-accent-700 hover:text-accent-800 text-sm font-medium transition-colors"
        >
          Mot de passe oublié&nbsp;?
        </Link>
      </div>

      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={isSubmitting || login.isPending}
        loadingLabel="Connexion en cours…"
      >
        Se connecter
      </Button>
    </form>
  );
};
